import { useEffect, useMemo } from "react"
import { Paper, Stack, Typography } from "@mui/material"
import { GridFilterModel } from "@mui/x-data-grid-premium"
import { LeadCountData } from "../table/leadTableTypes"
import { buildLeadsRequestBody, dashedToUnderscored, getIncludedStatusFilterValues, hasIncludedStatusFilter, type LeadCustomFieldTypes } from "../leadTableQuery"
import StatusFilterButton from "./StatusFilterButton"
import useLeadsRequest from "../table/useLeadsRequest"
import colors from "lib/colors"


export const DISPLAYED_LEAD_STATUSES = [
  'new-lead',
  'applied',
  'enrolled',
  'started',
  'filtered',
  'returned',
  'rejected',
  'error',
]

type LeadCountsApiResponse = {
  data: LeadCountData
}

type LeadCountRowProps = {
  filterModel: GridFilterModel
  timezone: string
  refreshKey: number
  onTotalChange: (total: number) => void
  onError: (error: string | null) => void
  onToggleStatusFilter: (status: string) => void
  customFieldTypes?: LeadCustomFieldTypes
}

export function LeadCountRow({
  filterModel,
  timezone,
  refreshKey,
  onTotalChange,
  onError,
  onToggleStatusFilter,
  customFieldTypes,
}: LeadCountRowProps) {
  const body = useMemo(
    () => buildLeadsRequestBody({ filterModel, timezone, customFieldTypes }),
    [filterModel, timezone, customFieldTypes]
  )

  const { data: countsResponse, error } = useLeadsRequest<LeadCountsApiResponse>(
    '/api/v1/leads/lead_counts',
    body,
    refreshKey //should this be unique for counts
  )

  useEffect(() => {
    onError(error ?? null)
  }, [error, onError])

  useEffect(() => {
    const total = countsResponse?.data?.total
    if (total != null) onTotalChange(total)
  }, [countsResponse, onTotalChange])

  const leadCount = countsResponse?.data?.total ?? 0
  const selectedStatuses = useMemo(
    () => getIncludedStatusFilterValues(filterModel),
    [filterModel]
  )

  const shouldShowAllCounts = !hasIncludedStatusFilter(filterModel)

  const filteredStatuses = useMemo(
    () => countsResponse?.data?.filtered_statuses ?? [],
    [countsResponse]
  )

  const leadStatusCounts = useMemo(() => {
    const rawCounts = countsResponse?.data?.counts_by_status ?? {}
    return filteredStatuses.reduce<Record<string, number>>(
      (acc, status) => {
        if (!(status in acc)) acc[status] = 0
        return acc
      },
      { ...rawCounts }
    )
  }, [countsResponse, filteredStatuses])

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      gap={3}
      alignItems='stretch'
      sx={{ mb: 3, flexShrink: 0 }}
    >
      <Paper
        sx={{
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
          flex: {
            xs: '1 1 auto',
            md: '0 0 180px',
          },
        }}
      >
        <Typography variant='body2' sx={{ color: colors.slateGray }}>Total Leads</Typography>
        <Typography variant='h1' sx={{ fontWeight: 700, fontSize: 24 }}>
          {leadCount.toLocaleString()}
        </Typography>
      </Paper>

      <Paper
        sx={{
          p: 2.5,
          flex: '1 1 auto',
          minWidth: 0,
        }}
      >
        <Typography variant='body2' sx={{ color: colors.slateGray }}>Lead Statuses</Typography>

        <Stack
          direction='row'
          gap={1}
          alignItems='center'
          flexWrap='wrap'
          sx={{ mt: 1 }}
        >
          {DISPLAYED_LEAD_STATUSES.map((status) => {
            const filterStatus = dashedToUnderscored(status)
            const thisStatusSelected = selectedStatuses.includes(filterStatus)
            const statusCount = leadStatusCounts[filterStatus]

            const shouldRenderCount =
              typeof statusCount === 'number' &&
              (shouldShowAllCounts || thisStatusSelected)

            return (
              <StatusFilterButton
                key={status}
                status={status}
                count={statusCount ?? 0}
                showCount={shouldRenderCount}
                selected={thisStatusSelected}
                onClick={() => onToggleStatusFilter(status)}
              />
            )
          })}
        </Stack>
      </Paper>
    </Stack>
  )
}
